/**
 * Guardrails rule catalog — single source of truth for the dashboard.
 *
 * Mirrors the reference design in
 * `docs/reference/Foyer_Dashboard_CURRENT.jsx` (line 4032 RULE_CATEGORIES
 * + line 4041 INITIAL_RULES). Six categories cover the 28 typed rules
 * declared by @sill/types `RuleType` plus the `r_custom` DSL escape valve.
 *
 * Implementation status (per rule):
 *   - 'shipped'  — the evaluator has a handler and the edge worker
 *                  enforces it on live traffic. The Add Rule modal
 *                  exposes its typed params form.
 *   - 'planned'  — the catalog entry exists but no handler ships yet.
 *                  Surfaced read-only in the modal with a "Coming soon"
 *                  badge so the merchant sees the roadmap but cannot
 *                  publish an unenforceable rule.
 *
 * Adding handlers: implement under `packages/policy/src/rules/<rule>.ts`,
 * wire into `packages/policy/src/index.ts`, flip status to 'shipped'
 * here, and add a `paramsSchema` entry below for the modal form.
 */

import {
  Activity,
  AlertOctagon,
  DollarSign,
  Fingerprint,
  Lock,
  ShieldAlert,
  Skull,
  type LucideIcon,
} from 'lucide-react';

import type { RuleCategory, RuleType } from '@sill/types';

import type { PillTone } from '../../components/atoms/index.js';

export interface CategoryDef {
  id: RuleCategory;
  label: string;
  /** Short tagline shown under the label in the picker. */
  tagline: string;
  icon: LucideIcon;
  tone: PillTone;
}

export const CATEGORIES: readonly CategoryDef[] = [
  {
    id: 'agent_identity',
    label: 'Agent identity',
    tagline: 'Who can call',
    icon: Fingerprint,
    tone: 'sky',
  },
  {
    id: 'rate_limit',
    label: 'Rate limits',
    tagline: 'How fast',
    icon: Activity,
    tone: 'amber',
  },
  {
    id: 'spend',
    label: 'Spend caps',
    tagline: 'How much',
    icon: DollarSign,
    tone: 'lime',
  },
  {
    id: 'scope',
    label: 'Scope',
    tagline: 'What agents can touch',
    icon: Lock,
    tone: 'mint',
  },
  {
    id: 'dark_pattern',
    label: 'Dark patterns',
    tagline: 'Deceptive-practice guardrails',
    icon: Skull,
    tone: 'coral',
  },
  {
    id: 'prompt_injection',
    label: 'Prompt injection',
    tagline: 'Hostile text in orders',
    icon: ShieldAlert,
    tone: 'coral',
  },
  {
    id: 'custom',
    label: 'Custom DSL',
    tagline: 'Rules you write yourself',
    icon: AlertOctagon,
    tone: 'neutral',
  },
];

export type RuleStatus = 'shipped' | 'planned';

/** Param-form variant for the Add Rule modal. */
export type ParamsForm =
  | { kind: 'none' } // no merchant-tunable params (just enable + action)
  | { kind: 'r01' } // agent allowlist (comma-separated)
  | { kind: 'r05' } // max amount in whole USD
  | { kind: 'r06' } // daily spend cap per user (whole USD) — origin-enforced
  | { kind: 'r07' } // max auto-approve (whole USD) for destructive actions
  | { kind: 'r31' } // cross-agent org budget (whole USD) + window — origin-enforced
  | { kind: 'r32' } // per-agent daily spend (whole USD) + window + overrides — origin-enforced
  | { kind: 'r12' } // geofence — allow/deny + ISO country list
  | { kind: 'r18' } // skill-manifest integrity — approved digest allow-list
  | { kind: 'r23' } // max validity window seconds
  | { kind: 'r25' } // max signed-body bytes
  | { kind: 'r28' } // kill switch — toggle-only
  | { kind: 'r29' } // delegation chain — require toggle + depth + head allowlist
  | { kind: 'r_custom' }; // DSL expression

export interface RuleDef {
  type: RuleType;
  category: RuleCategory;
  /** Short human label rendered in the rule list + modal. */
  name: string;
  /** One-line description shown under the name. */
  description: string;
  /** Reference standard or external citation (AP2 §X, OWASP API1, …). */
  reference?: string;
  status: RuleStatus;
  /** Form variant used by the Add Rule modal. */
  params: ParamsForm;
  /**
   * Where the rule's enforcement actually lives (ADR-0032 §4.1).
   *
   * Defaults to `'policy'` when absent: the rule has a handler in
   * `packages/policy/src/rules/` and the evaluator enforces it.
   *
   * A non-`'policy'` value means the rule is enforced OUTSIDE the policy
   * evaluator, so no policy-layer handler exists and the rule cannot be
   * tested from the dashboard QuickTest panel or toggled by editing a
   * policy:
   *   - `'verifier'`  — enforced at the verifier layer (ADR-0029) BEFORE
   *                     policy evaluation. r11 (mandate replay) is the example.
   *   - `'webhook'`   — enforced in the inbound-webhook handler (HMAC-SHA256
   *                     against the rotating secret). r27 is the example.
   *   - `'response'`  — enforced on the agent's OUTBOUND response, after the
   *                     mandate the evaluator sees. Post-response hook, not a
   *                     policy handler. r21 (output sanitization) is here.
   *   - `'dashboard'` — enforced in a human dashboard-user flow (gated on the
   *                     USER's role, which an agent mandate never carries).
   *                     r26 (refund authorization window) is here.
   *   - `'origin'`    — merchant-CONFIGURED (carries params) but SKIPPED at
   *                     the edge evaluator and enforced at the ORIGIN rail
   *                     executor (needs Postgres state the edge can't run).
   *                     r06 (daily spend cap per user) is here.
   *   - `'edge'`      — always-on EDGE INGRESS guard, enforced before the
   *                     policy evaluator runs (and on pre-mandate methods the
   *                     evaluator never sees). Not merchant-configurable and
   *                     not QuickTestable. r30 (MCP session rate limit,
   *                     ADR-0135) is here.
   */
  enforcementLayer?:
    | 'verifier'
    | 'policy'
    | 'webhook'
    | 'response'
    | 'dashboard'
    | 'origin'
    | 'edge';
}

/**
 * Can this rule be ADDED to a policy? True only for shipped rules whose
 * enforcement is policy-configurable: `policy`-layer (edge evaluator
 * handlers) and `origin`-layer (configured in the policy, enforced at the
 * rail executor — r06). Everything else (`verifier`/`webhook`/`response`/
 * `dashboard`) is enforced automatically and always-on — the server's
 * publish gate rejects them (`rule_N_type_unsupported`), so the modal must
 * never offer them as addable (the r21 draft-save 400, 2026-06-10).
 */
export function isPolicyConfigurable(rdef: RuleDef): boolean {
  return (
    rdef.status === 'shipped'
    && (rdef.enforcementLayer === undefined
      || rdef.enforcementLayer === 'policy'
      || rdef.enforcementLayer === 'origin')
  );
}

// ---------------------------------------------------------------------------
// 28-rule catalog ported from `Foyer_Dashboard_CURRENT.jsx:4041 INITIAL_RULES`
// plus the `r_custom` escape valve.
//
// Handler status is the source of truth for whether the policy evaluator
// actually enforces the rule. The four currently 'shipped' types match
// `packages/policy/src/rules/`:
//   - r01_agent_allowlist.ts
//   - r05_max_amount.ts
//   - r23_expires_at.ts
//   - r_custom.ts (DSL)
//
// The 24 'planned' entries appear in the modal so merchants can preview
// the roadmap and so the catalog stays aligned with the reference. They
// can NOT be added to a published policy until their handler ships.
// ---------------------------------------------------------------------------

export const RULES: readonly RuleDef[] = [
  // -- agent_identity (6) --
  {
    type: 'r01', category: 'agent_identity',
    name: 'Allowlisted agents only',
    description: 'Only agents on your approved list can transact. Everything else is rejected.',
    reference: 'AP2 §4 / OWASP Agentic ASI03',
    status: 'shipped',
    params: { kind: 'r01' },
  },
  {
    type: 'r02', category: 'agent_identity',
    name: 'Require a signed order from the customer',
    description: 'Every charge must carry an authorization your customer signed. No signed authorization, no sale.',
    reference: 'AP2 §4',
    status: 'shipped',
    params: { kind: 'none' },
  },
  {
    type: 'r11', category: 'agent_identity',
    name: 'Block replayed orders',
    // Review 2026-10-29 — merchant-plain name + description. r11 is
    // enforced at the verifier; the "always on" signal moves to the row
    // chip, so the description can be about what the rule does, not when.
    description: 'If the same signed order is submitted twice, the second copy is auto-rejected.',
    reference: 'AP2 §6.1',
    status: 'shipped',
    params: { kind: 'none' },
    // r11 enforcement lives at the verifier per ADR-0032 §3. The catalog
    // entry exists for visibility; the rule has no policy-layer handler
    // and cannot be tested from the dashboard QuickTest panel.
    enforcementLayer: 'verifier',
  },
  {
    type: 'r12', category: 'agent_identity',
    name: 'Country allow / deny',
    // ADR-0083 shipped the stateless geofence (CF edge country, fail-closed
    // on unknown); review 2026-10-29 switches to the plain merchant name.
    description: 'Allow or block requests by country. If the country can’t be determined, the request is blocked.',
    reference: 'OWASP API8:2023',
    status: 'shipped',
    params: { kind: 'r12' },
  },
  {
    type: 'r23', category: 'agent_identity',
    name: 'Short-lived order authorizations',
    description: 'Reject orders that stay valid for longer than your limit. A short window means a stolen authorization is useful for less time.',
    reference: 'AP2 §6.2',
    status: 'shipped',
    params: { kind: 'r23' },
  },
  {
    type: 'r29', category: 'agent_identity',
    name: 'Agent-to-agent handoff rules',
    // ADR-0091 — the merchant-tunable half of the delegation-chain split.
    // Chain VALIDITY (per-link ed25519 by registered delegator keys,
    // contiguity, windows, monotone scope narrowing) is always-on at the
    // verifier when a chain is present; this rule adds the requirements:
    // require proof for delegated principals, cap handoff depth, pin the
    // chain head to an allowlist.
    description: 'When one agent hands off to another, require signed proof. Optionally cap handoff depth and pin who can start a chain.',
    reference: 'OWASP Agentic ASI07',
    status: 'shipped',
    params: { kind: 'r29' },
  },
  {
    type: 'r27', category: 'agent_identity',
    name: 'Signed webhooks only',
    description: 'Incoming webhooks must carry a valid signature — forged webhook calls are rejected.',
    status: 'shipped',
    params: { kind: 'none' },
    // r27 is enforced in services/api/.../webhooks/stripe-webhooks.ts (Gate-0
    // dual-secret HMAC), not by a policy handler. The catalog entry exists for
    // visibility; it is not testable from QuickTest.
    enforcementLayer: 'webhook',
  },

  // -- rate_limit (4) --
  {
    type: 'r03', category: 'rate_limit',
    name: '60 calls per minute, per agent',
    description: 'Caps each agent at 60 calls per minute. Fixed limit.',
    reference: 'OWASP API4',
    status: 'shipped',
    params: { kind: 'none' },
  },
  {
    type: 'r04', category: 'rate_limit',
    // Factual correction (review 2026-10-29): the catalog previously
    // named r04 "1,000 calls/hour per IP", but the actual edge binding
    // is 100 calls per 60 seconds per IP (CF RateLimit binding only
    // supports 10s / 60s periods — see r04 handler docblock). Name +
    // description now reflect the real enforcement.
    name: '100 calls per minute, per IP',
    description: 'Caps any single network address at 100 calls per minute. Fixed limit.',
    status: 'shipped',
    params: { kind: 'none' },
  },
  {
    type: 'r13', category: 'rate_limit',
    name: 'Site-wide rate cap',
    description: 'Caps total calls across every agent at about 5,000 per hour. Protects the site from a swarm.',
    status: 'shipped',
    params: { kind: 'none' },
  },
  {
    type: 'r24', category: 'rate_limit',
    name: 'Lock out after repeated auth failures',
    // ADR-0083 + ADR-0087 (Phase B, shipped): auth failures occur at the
    // verifier (stage 5), BEFORE policy evaluation (stage 7), so r24 is a
    // verifier-layer rule, not a policy handler. The lockout is 1h on an
    // IP after 5 failures / 60s — review 2026-10-29 keeps those numbers
    // (verified against the handler + the auth_lockout UPSERT in
    // services/api/.../process-mandate-draft.ts).
    description: 'An address that fails authentication 5 times in a minute is locked out for an hour. Caps credential stuffing.',
    reference: 'OWASP API2:2023',
    status: 'shipped',
    params: { kind: 'none' },
    enforcementLayer: 'verifier',
  },
  {
    type: 'r30', category: 'rate_limit',
    name: 'MCP discovery rate limit',
    // ADR-0135: always-on EDGE INGRESS guard on the pre-mandate MCP methods
    // (initialize / tools/list), which run before any mandate so the policy
    // evaluator (and r03/r04) never see them. Not a policy handler and not
    // merchant-configurable; enforced at apps/edge/src/mcp/handler.ts via the
    // MCP_SESSION_RATELIMIT binding. Closes NSA MCP §DoS / OWASP MCP02.
    description: 'Caps how often an agent can open an MCP session and list your tools. Protects discovery from flooding.',
    reference: 'OWASP MCP02',
    status: 'shipped',
    params: { kind: 'none' },
    enforcementLayer: 'edge',
  },

  // -- spend (6) --
  {
    type: 'r05', category: 'spend',
    name: 'Per-transaction spend cap',
    description: 'Set a spend cap for each currency you accept. No FX conversion. Unlisted currencies: reject or allow, your choice.',
    reference: 'AP2 §5',
    status: 'shipped',
    params: { kind: 'r05' },
  },
  {
    type: 'r06', category: 'spend',
    name: 'Daily spend cap, per customer',
    // ADR-0085: configured here (a USD cap) but ENFORCED at the origin rail
    // executor — the edge skips it (the 24h spend SUM is a Postgres
    // aggregate the edge can't run). Keyed on the signed user_ref's derived
    // key; USD-only; fail-soft when no user is identified. Requires
    // SILL_USER_KEY_SALT on edge + origin.
    description: 'Caps how much any single customer can spend through agents in a rolling 24 hours (USD). Checked at charge time.',
    reference: 'OWASP API4:2023',
    status: 'shipped',
    enforcementLayer: 'origin',
    params: { kind: 'r06' },
  },
  {
    type: 'r31', category: 'spend',
    name: 'Budget cap, per customer organization',
    // ADR-0151: configured here (a USD cap + rolling window) but ENFORCED
    // at the origin in the rail-neutral consumer (`process-mandate-draft`)
    // AFTER `isSettlementAuthorizing` and BEFORE `railSelector.select()`,
    // so every current and future settlement rail is covered by
    // construction. Reads the unified `settlement_ledger` (each rail
    // writes one row on settle and one negative-amount row on refund —
    // SUM is naturally net). Keyed on the signed `principal.org_ref`'s
    // derived `org_key`; USD-only; fail-soft when no organization is
    // identified. Requires SILL_ORG_KEY_SALT on edge + origin (separate
    // salt from SILL_USER_KEY_SALT).
    description: 'Caps how much one customer-side organization can spend across all your agents in a rolling window (USD).',
    reference: 'GDM AI Control Roadmap / TRAIT&R',
    status: 'shipped',
    enforcementLayer: 'origin',
    params: { kind: 'r31' },
  },
  {
    type: 'r32', category: 'spend',
    name: 'Daily spend cap, per agent',
    // ADR-0260: configured here (a USD cap + rolling window + optional
    // per-agent overrides) but ENFORCED at the origin in the rail-neutral
    // consumer immediately AFTER the r31 gate. Reads the unified
    // `settlement_ledger` keyed on `verified_agent_id` — the
    // registry-matched agent identity the mandate verifier produced at
    // verify-time (a verification OUTPUT, NOT a signed body field a key
    // holder could vary). A stolen signing key is bounded by this cap
    // until revocation. Non-USD charges pass through. Net settled spend
    // (refunds drop out). Default disposition is `escalate` (hold for
    // review via the existing HITL loop); `reject` is available per site.
    description: 'Caps how much any one agent can spend in a rolling 24 hours (USD). Keyed to the agent’s signing key, so a stolen key stays bounded until you revoke it. Refunds drop out of the total; non-USD charges pass through.',
    reference: 'MITRE ATLAS T0139',
    status: 'shipped',
    enforcementLayer: 'origin',
    params: { kind: 'r32' },
  },
  {
    type: 'r14', category: 'spend',
    name: 'Cart stays under the customer’s budget',
    description: 'Reject any cart whose total exceeds the budget your customer actually authorized.',
    reference: 'AP2 §5.2',
    status: 'shipped',
    params: { kind: 'none' },
  },
  {
    type: 'r15', category: 'spend',
    name: 'Cart currency matches the authorization',
    description: 'The cart currency must match what the customer authorized — a $100 budget can’t silently become £100.',
    reference: 'AP2 §5.2',
    status: 'shipped',
    params: { kind: 'none' },
  },
  {
    type: 'r16', category: 'spend',
    name: 'Signed cart cannot be edited',
    // ADR-0093 — the cart (line items, total, currency) is part of the
    // signed mandate body; the mandate signature makes it tamper-evident
    // and the cart cannot change after the customer authorizes it. A
    // cross-mandate (AP2 quote→charge) immutability check is a separate
    // future capability — see ADR-0093.
    description: 'The cart your customer authorizes — items, total, currency — is signed and cannot be altered before the charge.',
    status: 'shipped',
    params: { kind: 'none' },
    enforcementLayer: 'verifier',
  },
  {
    type: 'r26', category: 'spend',
    name: 'Refund authorization window',
    // Gated on a human dashboard-USER's role (Owner) + the original
    // transaction's age. An agent mandate carries no Owner/Admin role, so
    // this is not an agent-policy rule — it belongs in the dashboard refund-
    // authorization flow (where a human user with a role acts). Tagged
    // 'dashboard' layer; stays 'planned' until that flow exists. See ADR-0083.
    description: 'Refunds on orders older than 90 days need an owner’s approval in the dashboard.',
    status: 'planned',
    params: { kind: 'none' },
    enforcementLayer: 'dashboard',
  },

  // -- scope (5) --
  {
    type: 'r07', category: 'scope',
    name: 'Human review for refunds and cancellations',
    description: 'Refunds and cancellations auto-approve only up to a per-currency ceiling. Anything over goes to a human.',
    status: 'shipped',
    params: { kind: 'r07' },
  },
  {
    type: 'r17', category: 'scope',
    name: 'One customer, one customer’s data',
    // ADR-0084: enforces strict self-scoping — when an intent targets a
    // specific customer object (`intent.target_user_ref`), the acting user
    // (`principal.user_ref`) must match it, else it's cross-customer (BOLA)
    // access. Keys are compared via the salted deriveUserKey (no raw PII).
    // Fail-safe: target present but no acting user → reject. Requires
    // SILL_USER_KEY_SALT configured (edge + origin) or it fails closed.
    description: 'An agent acting for one customer can only touch that customer’s own orders. Cross-customer reads are blocked.',
    reference: 'OWASP API1 (BOLA)',
    status: 'shipped',
    params: { kind: 'none' },
  },
  {
    type: 'r18', category: 'scope',
    name: 'Approved agent toolsets only',
    // ADR-0086 — manifest pinning (the agent equivalent of Subresource
    // Integrity). The agent commits a digest of its active skill manifest into
    // the signed mandate (protocol_context.skill_manifest_hash); r18 rejects
    // any digest not on the merchant's approved allow-list, and fail-safe
    // rejects a mandate carrying no digest. Transactional-only.
    description: 'Each agent’s toolset has a fingerprint. Only fingerprints you’ve approved pass. New or tampered toolsets are blocked until you approve them.',
    reference: 'MITRE ATLAS AML.T0011.002',
    status: 'shipped',
    params: { kind: 'r18' },
  },
  {
    type: 'r25', category: 'scope',
    name: 'Order payload size limit',
    description: 'Reject orders larger than the size limit you set (in bytes).',
    status: 'shipped',
    params: { kind: 'r25' },
  },
  {
    type: 'r28', category: 'scope',
    name: 'Emergency kill switch',
    description: 'When enabled, refuses every order for this site. Use for incident response — flip back off to resume.',
    status: 'shipped',
    params: { kind: 'r28' },
  },

  // -- dark_pattern (3) --
  {
    type: 'r08', category: 'dark_pattern',
    name: 'No fake-urgency orders',
    description: 'Reject orders carrying manipulative urgency: "limited time", "only X left", countdown-timer language.',
    reference: 'UK CMA / FTC 16 CFR 464',
    status: 'shipped',
    params: { kind: 'none' },
  },
  {
    type: 'r09', category: 'dark_pattern',
    name: 'No drip pricing',
    description: 'The checkout total must match the price quoted — no fees silently added along the way.',
    reference: 'UK DMCC Act 2024',
    status: 'shipped',
    params: { kind: 'none' },
  },
  {
    type: 'r19', category: 'dark_pattern',
    name: 'No silent subscriptions',
    description: 'Recurring charges need their own user-signed authorization. Blocks one-off orders flipping to subscription silently.',
    status: 'shipped',
    params: { kind: 'none' },
  },

  // -- prompt_injection (4) --
  {
    type: 'r10', category: 'prompt_injection',
    name: 'Block instruction-override text',
    description: 'Reject orders carrying prompt-injection text — "ignore previous instructions", hidden control tags, jailbreak attempts.',
    reference: 'OWASP LLM01:2026',
    status: 'shipped',
    params: { kind: 'none' },
  },
  {
    type: 'r20', category: 'prompt_injection',
    name: 'Block invisible-character attacks',
    description: 'Reject orders with invisible Unicode characters that can smuggle hidden instructions past human reviewers.',
    reference: 'Goodside 2024 / CVE-2024-5184',
    status: 'shipped',
    params: { kind: 'none' },
  },
  {
    type: 'r21', category: 'prompt_injection',
    name: 'Scrub agent-visible responses',
    description: 'Everything sent back to an agent is auto-scrubbed: secrets (API keys, tokens), invisible characters, and planted "ignore your instructions" text are removed before the agent sees them.',
    reference: 'OWASP LLM02:2026 / LLM05:2026',
    status: 'shipped',
    params: { kind: 'none' },
    // r21 acts on the agent's OUTBOUND response, which the policy evaluator
    // never sees. It enforces unconditionally at the edge wired-dispatch
    // seam (ADR-0090, the r27 always-on pattern) — never a policy handler.
    enforcementLayer: 'response',
  },
  {
    type: 'r22', category: 'prompt_injection',
    name: 'Block orders that contain secrets',
    description: 'Reject orders with secrets embedded — AWS keys, GitHub or Stripe tokens, and other credentials.',
    status: 'shipped',
    params: { kind: 'none' },
  },

  // -- custom (1) --
  {
    type: 'r_custom', category: 'custom',
    name: 'Your own rule (advanced)',
    description: 'A rule you write yourself against the verified order. Advanced — see the rule-language reference.',
    status: 'shipped',
    params: { kind: 'r_custom' },
  },
];

export function rulesForCategory(category: RuleCategory): readonly RuleDef[] {
  return RULES.filter((r) => r.category === category);
}

export function ruleByType(type: RuleType): RuleDef | undefined {
  return RULES.find((r) => r.type === type);
}

export function categoryById(id: RuleCategory): CategoryDef | undefined {
  return CATEGORIES.find((c) => c.id === id);
}
